/**
 * `instant.ts` — the single chokepoint for the canonical day axis.
 *
 * Per **ADR-0006 §1**, in-world time is a signed integer day count
 * (`Number.MAX_SAFE_INTEGER` ≈ ±24.6 trillion years — ~1,800× the age of
 * the universe; sub-day resolution lives in a *separate* `seconds_into_day`
 * field, so the day budget is never consumed by time-of-day). To keep the
 * representation swappable in one place if the ceiling is ever wrong, the
 * value is a **branded type** and ALL day arithmetic + (de)serialization
 * routes through this module. No bare arithmetic on a `CanonicalDay` at
 * call sites.
 *
 * This module also owns:
 *
 *   - the **half-open `[from, to)`** interval helpers (membership,
 *     overlap, adjacency) consumed by the valid-time segment work
 *     (ADR-0005 §4); and
 *   - the **inclusive ↔ exclusive** conversion used at the authoring /
 *     display boundary: an author saying "valid through day X" stores the
 *     half-open upper bound `X + 1` (`toExclusiveEnd`), and a stored
 *     `[…, to)` renders back as "through `to − 1`" (`toInclusiveEnd`).
 *
 * Everything here is pure and JSON-native; the brand is erased at runtime
 * (a `CanonicalDay` *is* a `number`), so serialization is the identity
 * function plus a validating parse.
 */

/**
 * The canonical day count: a signed integer offset on the shared,
 * calendar-independent day axis (ADR-0006 §1–§2). Negative values are
 * pre-anchor (deep history). Branded so the representation is owned by
 * this module — construct one only via {@link canonicalDay} /
 * {@link parseCanonicalDay} / the arithmetic helpers below.
 */
export type CanonicalDay = number & { readonly __brand: 'CanonicalDay' };

/** The shared axis origin (`anchor_canonical_day: 0`), as a typed constant. */
export const CANONICAL_DAY_ZERO = 0 as CanonicalDay;

// ---------- construction + (de)serialization ----------

/**
 * Brand a raw integer as a {@link CanonicalDay}. Throws on a
 * non-integer / non-finite / out-of-safe-range input — the axis is
 * integral by definition (sub-day resolution lives in a separate field,
 * ADR-0006 §1).
 */
export function canonicalDay(value: number): CanonicalDay {
  if (!Number.isInteger(value)) {
    throw new RangeError(`CanonicalDay must be an integer, got ${value}`);
  }
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`CanonicalDay out of safe-integer range: ${value}`);
  }
  return value as CanonicalDay;
}

/** Type guard: is `value` a valid canonical-day representation? */
export function isCanonicalDay(value: unknown): value is CanonicalDay {
  return typeof value === 'number' && Number.isSafeInteger(value);
}

/**
 * Serialize a {@link CanonicalDay} to its JSON-native form. The brand is
 * erased at runtime, so this is the identity on the underlying `number`;
 * it exists as the named chokepoint so the wire shape is owned here.
 */
export function serializeCanonicalDay(day: CanonicalDay): number {
  return day as number;
}

/**
 * Parse a JSON-native value back into a {@link CanonicalDay}. Throws if
 * the value is not a safe integer. Use at every deserialization boundary
 * (op log, wire envelopes, stored validity bounds).
 */
export function parseCanonicalDay(value: number): CanonicalDay {
  return canonicalDay(value);
}

// ---------- day arithmetic (the only place bare +/- happens) ----------

/** Add a whole number of days to a canonical day. */
export function addDays(day: CanonicalDay, days: number): CanonicalDay {
  return canonicalDay((day as number) + days);
}

/** Subtract a whole number of days from a canonical day. */
export function subtractDays(day: CanonicalDay, days: number): CanonicalDay {
  return canonicalDay((day as number) - days);
}

/** The signed number of days from `a` to `b` (`b - a`). */
export function daysBetween(a: CanonicalDay, b: CanonicalDay): number {
  return (b as number) - (a as number);
}

/** `a < b` on the canonical axis. */
export function isBefore(a: CanonicalDay, b: CanonicalDay): boolean {
  return (a as number) < (b as number);
}

/** `a > b` on the canonical axis. */
export function isAfter(a: CanonicalDay, b: CanonicalDay): boolean {
  return (a as number) > (b as number);
}

/** Structural equality of two canonical days. */
export function isSameDay(a: CanonicalDay, b: CanonicalDay): boolean {
  return (a as number) === (b as number);
}

/** Numeric comparator for sorting canonical days ascending. */
export function compareCanonicalDay(a: CanonicalDay, b: CanonicalDay): number {
  return (a as number) - (b as number);
}

// ---------- half-open [from, to) intervals (ADR-0005 §4) ----------

/**
 * A half-open in-world interval `[from, to)` — `from` is inclusive, `to`
 * is exclusive. Either bound may be `null` to mean unbounded in that
 * direction (open start = "since the beginning of time"; open end =
 * "still valid"). This is the canonical valid-time segment shape; the
 * exclusive upper bound is what makes back-to-back segments *adjacent*
 * (touching) without *overlapping*.
 */
export interface CanonicalRange {
  /** Inclusive lower bound; `null` = unbounded into the past. */
  readonly from: CanonicalDay | null;
  /** Exclusive upper bound; `null` = unbounded into the future. */
  readonly to: CanonicalDay | null;
}

/**
 * Construct a half-open range, validating `from < to` when both bounds
 * are present (a zero-width or inverted `[from, to)` carries no days and
 * is rejected — an explicit empty interval is not representable here by
 * design; use `null` bounds for open-endedness).
 */
export function rangeOf(from: CanonicalDay | null, to: CanonicalDay | null): CanonicalRange {
  if (from !== null && to !== null && (to as number) <= (from as number)) {
    throw new RangeError(
      `half-open range requires from < to, got [${from as number}, ${to as number})`,
    );
  }
  return { from, to };
}

/**
 * Membership test for the half-open interval: `from <= day < to`. An
 * absent `from` admits everything below `to`; an absent `to` admits
 * everything at/above `from`. The day exactly equal to `to` is NOT a
 * member — that is the whole point of the exclusive upper bound.
 */
export function membership(day: CanonicalDay, range: CanonicalRange): boolean {
  if (range.from !== null && (day as number) < (range.from as number)) return false;
  if (range.to !== null && (day as number) >= (range.to as number)) return false;
  return true;
}

/**
 * Do two half-open intervals overlap? `[a.from, a.to)` and
 * `[b.from, b.to)` overlap iff `a.from < b.to && b.from < a.to`, with
 * `null` bounds treated as ∓∞. A **shared boundary does NOT overlap**:
 * `[0, 5)` and `[5, 10)` touch at 5 but 5 belongs only to the second —
 * see {@link adjacent}.
 */
export function overlaps(a: CanonicalRange, b: CanonicalRange): boolean {
  // a.from < b.to  (b.to = +inf when null; a.from = -inf when null)
  const aStartsBeforeBEnds =
    a.from === null || b.to === null || (a.from as number) < (b.to as number);
  // b.from < a.to
  const bStartsBeforeAEnds =
    b.from === null || a.to === null || (b.from as number) < (a.to as number);
  return aStartsBeforeBEnds && bStartsBeforeAEnds;
}

/**
 * Are two half-open intervals **adjacent** — exactly touching at a shared
 * boundary with no gap and no overlap? `[0, 5)` and `[5, 10)` are
 * adjacent (a's exclusive end equals b's inclusive start, or vice
 * versa). Adjacency is mutually exclusive with {@link overlaps}: a shared
 * boundary is adjacency, never overlap. Open bounds (`null`) never form a
 * finite touching point and so are never adjacent.
 */
export function adjacent(a: CanonicalRange, b: CanonicalRange): boolean {
  const aEndTouchesBStart =
    a.to !== null && b.from !== null && (a.to as number) === (b.from as number);
  const bEndTouchesAStart =
    b.to !== null && a.from !== null && (b.to as number) === (a.from as number);
  return aEndTouchesBStart || bEndTouchesAStart;
}

// ---------- inclusive <-> exclusive conversion (authoring boundary) ----------

/**
 * Convert an author's **inclusive** "through day X" upper bound to the
 * **exclusive** half-open upper bound that gets stored: `X + 1`. This is
 * the single place that `+1` happens — every authoring surface routes its
 * end date through here so the stored segment is always `[…, X + 1)`.
 */
export function toExclusiveEnd(inclusiveThrough: CanonicalDay): CanonicalDay {
  return addDays(inclusiveThrough, 1);
}

/**
 * Convert a stored **exclusive** half-open upper bound back to the
 * author-facing **inclusive** "through day X" value: `to − 1`. The
 * inverse of {@link toExclusiveEnd}; display surfaces route the stored
 * upper bound through here before rendering "valid through …".
 */
export function toInclusiveEnd(exclusiveTo: CanonicalDay): CanonicalDay {
  return subtractDays(exclusiveTo, 1);
}
